/**
 * The library's search and filter controls, as one component system.
 *
 * What was there before worked and looked like the browser's defaults: a bare
 * `<input type="search">` and native `<select>`s with a border-radius on them.
 * The behaviour was right; the controls did not belong to SentyCap, and a
 * `<select>` cannot be made to - its popup is drawn by the operating system and
 * takes no styling, no checkmark and no section heading.
 *
 * So these are real popovers, and the cost of that is the work the platform
 * was doing for free. All of it is here rather than skipped:
 *
 * * **Keyboard.** Up/Down move through options, Home/End jump, Enter and Space
 *   choose, Escape closes and returns focus to the trigger, Tab closes and
 *   moves on. The listbox is not a focus trap - trapping focus in a filter
 *   menu is how a keyboard user gets stuck in a toolbar.
 * * **Screen readers.** The trigger is `aria-haspopup="listbox"` with
 *   `aria-expanded`; the list is `role="listbox"`, each option is
 *   `role="option"` with `aria-selected`, and the active option is pointed at
 *   by `aria-activedescendant` so the selection is announced without moving
 *   DOM focus off the list.
 * * **Dismissal.** Click outside, Escape, and choosing an option all close it.
 *
 * `BooleanFilter` exists because a three-state choice does not deserve a
 * dropdown. "Tümü / Var / Yok" is a segmented control inside the popover: all
 * three states visible at once, one click to change, no menu to open twice to
 * find out what is currently set. It shares the trigger, the surface and the
 * focus treatment with the others so the toolbar still reads as one thing.
 */

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { IconChevronDown, IconCheck, IconSearch } from "./icons";
import "./filters.css";

/* --- search ---------------------------------------------------------------- */

export function FilterSearch({
  value,
  onChange,
  placeholder,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  label: string;
}) {
  const input = useRef<HTMLInputElement>(null);

  return (
    <div className={`fsearch${value ? " fsearch--filled" : ""}`}>
      <IconSearch size={16} />
      <input
        ref={input}
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          // Escape clears rather than blurring: on a filter field the text is
          // the state, and clearing it is what the key is for here.
          if (event.key === "Escape" && value) {
            event.stopPropagation();
            onChange("");
          }
        }}
        placeholder={placeholder}
        aria-label={label}
        // `type="text"`, not `type="search"`: the native search input draws its
        // own clear button, which cannot be styled and would sit beside ours.
        autoComplete="off"
        spellCheck={false}
      />
      {value && (
        <button
          type="button"
          className="fsearch__clear"
          onClick={() => {
            onChange("");
            input.current?.focus();
          }}
          aria-label="Aramayı temizle"
        >
          ×
        </button>
      )}
    </div>
  );
}

/* --- popover shell --------------------------------------------------------- */

function useDismiss(open: boolean, close: () => void, root: React.RefObject<HTMLElement>) {
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) close();
    };
    // `mousedown`, not `click`: closing on click would fire after a control
    // inside the popover had already handled the same gesture.
    window.addEventListener("mousedown", onPointer);
    return () => window.removeEventListener("mousedown", onPointer);
  }, [open, close, root]);
}

export interface FilterOption<T extends string> {
  value: T;
  label: string;
}

/** The button every filter shares. Shows its own value; `×` clears it. */
function Trigger({
  label,
  value,
  active,
  open,
  listId,
  onToggle,
  onClear,
  triggerRef,
}: {
  label: string;
  value: string;
  active: boolean;
  open: boolean;
  listId: string;
  onToggle: () => void;
  onClear?: (() => void) | undefined;
  triggerRef: React.RefObject<HTMLButtonElement>;
}) {
  return (
    <div className={`ftrigger${active ? " ftrigger--active" : ""}`}>
      <button
        type="button"
        className="ftrigger__button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={onToggle}
        ref={triggerRef}
      >
        <span className="ftrigger__label">{label}:</span>
        <span className="ftrigger__value">{value}</span>
        <IconChevronDown size={14} />
      </button>
      {active && onClear && (
        <button
          type="button"
          className="ftrigger__clear"
          onClick={onClear}
          aria-label={`${label} filtresini kaldır`}
        >
          ×
        </button>
      )}
    </div>
  );
}

/* --- list filter ----------------------------------------------------------- */

export function FilterSelect<T extends string>({
  label,
  value,
  options,
  defaultValue,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly FilterOption<T>[];
  /** The value that counts as "not filtering". Never shows a clear button. */
  defaultValue: T;
  onChange: (value: T) => void;
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const id = useId();
  const listId = `${id}-list`;

  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, root);

  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );
  const current = options[selectedIndex]?.label ?? "";
  const active = value !== defaultValue;

  useEffect(() => {
    if (open) {
      setActiveIndex(selectedIndex);
      list.current?.focus();
    }
  }, [open, selectedIndex]);

  const choose = (index: number) => {
    const option = options[index];
    if (option) onChange(option.value);
    setOpen(false);
    trigger.current?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActiveIndex((i) => Math.min(options.length - 1, i + 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setActiveIndex((i) => Math.max(0, i - 1));
        break;
      case "Home":
        event.preventDefault();
        setActiveIndex(0);
        break;
      case "End":
        event.preventDefault();
        setActiveIndex(options.length - 1);
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        choose(activeIndex);
        break;
      case "Escape":
        event.preventDefault();
        setOpen(false);
        trigger.current?.focus();
        break;
      case "Tab":
        // Close and let focus move on. Trapping it here would strand a
        // keyboard user inside a filter menu.
        setOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <div className="fcontrol" ref={root}>
      <Trigger
        label={label}
        value={current}
        active={active}
        open={open}
        listId={listId}
        onToggle={() => setOpen((was) => !was)}
        onClear={active ? () => onChange(defaultValue) : undefined}
        triggerRef={trigger}
      />
      {open && (
        <ul
          id={listId}
          className="fpopover"
          role="listbox"
          aria-label={label}
          aria-activedescendant={`${id}-option-${activeIndex}`}
          tabIndex={-1}
          ref={list}
          onKeyDown={onKeyDown}
        >
          {options.map((option, index) => (
            <li
              key={option.value}
              id={`${id}-option-${index}`}
              role="option"
              aria-selected={option.value === value}
              className={`fpopover__option${
                index === activeIndex ? " fpopover__option--active" : ""
              }`}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => choose(index)}
            >
              <span className="fpopover__tick" aria-hidden="true">
                {option.value === value && <IconCheck size={14} />}
              </span>
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* --- boolean filter -------------------------------------------------------- */

export function BooleanFilter<T extends string>({
  label,
  value,
  options,
  defaultValue,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly FilterOption<T>[];
  defaultValue: T;
  onChange: (value: T) => void;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const group = useRef<HTMLDivElement>(null);
  const id = useId();

  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, root);

  const current = options.find((option) => option.value === value)?.label ?? "";
  const active = value !== defaultValue;

  useEffect(() => {
    if (open) group.current?.querySelector<HTMLElement>("[data-checked='true']")?.focus();
  }, [open]);

  return (
    <div className="fcontrol" ref={root}>
      <Trigger
        label={label}
        value={current}
        active={active}
        open={open}
        listId={`${id}-group`}
        onToggle={() => setOpen((was) => !was)}
        onClear={active ? () => onChange(defaultValue) : undefined}
        triggerRef={trigger}
      />
      {open && (
        <div
          id={`${id}-group`}
          className="fpopover fpopover--segmented"
          role="radiogroup"
          aria-label={label}
          ref={group}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              setOpen(false);
              trigger.current?.focus();
            }
          }}
        >
          {/*
            Three radios, not a menu. All the states are visible at once, so
            the current one is readable without opening anything twice - which
            is the whole reason a three-way choice should not be a dropdown.
          */}
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={option.value === value}
              data-checked={option.value === value}
              className={`fsegment${option.value === value ? " fsegment--on" : ""}`}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
                trigger.current?.focus();
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
